import { useCallback, useEffect, useState } from 'react';
import { Archive, Pencil, Share2, TimerOff } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BackofficePageModeActionMenu,
  CopyIdButton,
  CustomizableDetailTabs,
  DetailPanel,
  DetailPanelGrid,
  PageHeader,
  copyTextToClipboard,
  type PanelSpan,
  type TableActionItem,
  panelSpanFromLayout,
  useCustomizableDetailTabs,
  useBackofficePageMode,
} from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import {
  fetchBackofficeVoucherDetail,
  fetchBackofficeVoucherRedemptions,
  updateBackofficeVoucher,
} from './api';
import { CreateVoucherModal, VoucherStatusChip } from './components';
import type {
  BackofficeVoucher,
  BackofficeVoucherRedemptionRow,
  UpdateBackofficeVoucherInput,
} from './types';

function formatShortId(value: string): string {
  const [firstPart] = value.split('-');
  return firstPart || value;
}

function formatDateTime(value: string | null): string {
  if (!value) return 'Not set';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value));
}

function formatStatus(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (first) => first.toUpperCase());
}

function formatRedemptionMode(value: BackofficeVoucher['redemptionMode']): string {
  if (value === 'single_use') return 'Single use';
  if (value === 'multi_use_capped') return 'Capped multi-use';
  return 'Uncapped multi-use';
}

function isSingleUseRedeemed(voucher: BackofficeVoucher): boolean {
  return voucher.redemptionMode === 'single_use' && voucher.calculatedState === 'redeemed';
}

function isVoucherEditable(voucher: BackofficeVoucher): boolean {
  return (
    !['expired', 'archived'].includes(voucher.calculatedState) && !isSingleUseRedeemed(voucher)
  );
}

type VoucherTab = 'overview';

const VOUCHER_BUILT_IN_TABS = [{ id: 'overview', label: 'Overview' }] satisfies Array<{
  id: VoucherTab;
  label: string;
}>;

function DetailRow({
  label,
  value,
  copyValue,
  copyLabel,
}: {
  label: string;
  value: string;
  copyValue?: string;
  copyLabel?: string;
}) {
  return (
    <div className="grid gap-1 border-b border-neutral-100 py-3 last:border-b-0 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
      <dt className="font-semibold text-neutral-900">{label}</dt>
      <dd className="min-w-0 text-neutral-700">
        {copyValue && copyLabel ? (
          <div className="group inline-flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate">{value}</span>
            <CopyIdButton value={copyValue} label={copyLabel} />
          </div>
        ) : (
          <span>{value}</span>
        )}
      </dd>
    </div>
  );
}

function VoucherDetailsTile({
  voucher,
  code,
  currentSpan,
  panelId,
}: {
  voucher: BackofficeVoucher;
  code: string | null;
  currentSpan?: PanelSpan;
  panelId?: string | undefined;
}) {
  return (
    <DetailPanel title="Voucher details" span="2x3" currentSpan={currentSpan} panelId={panelId}>
      <dl className="text-body">
        <DetailRow
          label="ID"
          value={formatShortId(voucher.id)}
          copyValue={voucher.id}
          copyLabel="Copy voucher ID"
        />
        <DetailRow
          label="Code"
          value={voucher.codeDisplay}
          copyValue={code ?? voucher.codeDisplay}
          copyLabel="Copy voucher code"
        />
        <DetailRow
          label="Name"
          value={voucher.name}
          copyValue={voucher.name}
          copyLabel="Copy voucher name"
        />
        <DetailRow label="Status" value={formatStatus(voucher.calculatedState)} />
        <DetailRow
          label="Interviews"
          value={`${voucher.interviewQuantity} ${voucher.interviewQuantity === 1 ? 'interview' : 'interviews'}`}
        />
        <DetailRow label="Use type" value={formatRedemptionMode(voucher.redemptionMode)} />
        <DetailRow
          label="Redemptions"
          value={`${voucher.confirmedRedemptions}${voucher.maxRedemptions ? ` / ${voucher.maxRedemptions}` : ''}`}
        />
        <DetailRow label="Active from" value={formatDateTime(voucher.startsAt)} />
        <DetailRow label="Expires" value={formatDateTime(voucher.expiresAt)} />
        <DetailRow label="Created by" value={voucher.createdByEmail ?? 'Unknown'} />
        <DetailRow label="Created" value={formatDateTime(voucher.createdAt)} />
      </dl>
    </DetailPanel>
  );
}

function RedeemedByTile({
  redemptions,
  currentSpan,
  panelId,
}: {
  redemptions: BackofficeVoucherRedemptionRow[];
  currentSpan?: PanelSpan;
  panelId?: string | undefined;
}) {
  const navigate = useNavigate();

  return (
    <DetailPanel
      title="Redeemed by"
      span="2x3"
      currentSpan={currentSpan}
      panelId={panelId}
      contentOverflow="auto"
    >
      {redemptions.length === 0 ? (
        <div className="py-8 text-center text-body text-neutral-500">No redemptions yet.</div>
      ) : (
        <div className="divide-y divide-neutral-100">
          {redemptions.map((redemption) => {
            const userLabel = redemption.user.name ?? redemption.user.email ?? redemption.user.id;
            return (
              <button
                key={redemption.id}
                type="button"
                className="focus-ring block w-full px-1 py-3 text-left hover:bg-backoffice-50"
                onClick={() => navigate(`/organizations/${redemption.organization.id}`)}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-body font-semibold text-neutral-900">
                      {userLabel}
                    </div>
                    <div className="truncate text-small text-neutral-500">
                      {redemption.organization.name}
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-small text-neutral-500">
                    <div>{redemption.grantedInterviewQuantity} interviews</div>
                    <div>{formatDateTime(redemption.confirmedAt ?? redemption.createdAt)}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </DetailPanel>
  );
}

export function BackofficeVoucherDetailsPage() {
  const { voucherId } = useParams<{ voucherId: string }>();
  const location = useLocation();
  const [voucher, setVoucher] = useState<BackofficeVoucher | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [redemptions, setRedemptions] = useState<BackofficeVoucherRedemptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const { pageMode, setPageMode } = useBackofficePageMode();
  const detailBasePath = voucherId ? `/vouchers/${voucherId}` : '/vouchers';
  const detailTabs = useCustomizableDetailTabs<VoucherTab>({
    preferenceScope: 'details:voucher:tabs',
    builtInTabs: VOUCHER_BUILT_IN_TABS,
    defaultTabId: 'overview',
    basePath: detailBasePath,
    pageMode,
  });

  useBackofficePageTitle(voucher ? `Voucher: ${voucher.name}` : 'Voucher');

  const loadVoucher = useCallback(
    async (signal: AbortSignal) => {
      if (!voucherId) {
        throw new Error('Voucher not found');
      }

      const [detail, redeemedBy] = await Promise.all([
        fetchBackofficeVoucherDetail(voucherId, signal),
        fetchBackofficeVoucherRedemptions(
          voucherId,
          new URLSearchParams({ limit: '100', offset: '0' }),
          signal
        ),
      ]);
      setVoucher(detail.voucher);
      setCode(detail.code);
      setRedemptions(redeemedBy.redemptions);
    },
    [voucherId]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    loadVoucher(controller.signal)
      .catch((caught) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof Error ? caught.message : 'Failed to load voucher');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [loadVoucher]);

  async function handleUpdateVoucher(input: UpdateBackofficeVoucherInput) {
    if (!voucher) return;

    setUpdating(true);
    setUpdateError(null);
    try {
      const response = await updateBackofficeVoucher(voucher.id, input);
      setVoucher(response.voucher);
      setEditing(false);
    } catch (caught) {
      setUpdateError(caught instanceof Error ? caught.message : 'Failed to update voucher');
    } finally {
      setUpdating(false);
    }
  }

  async function handleExpireVoucher() {
    if (!voucher) return;
    const now = new Date().toISOString();
    await handleUpdateVoucher({ status: 'active', expiresAt: now });
  }

  async function handleArchiveVoucher() {
    if (!voucher) return;
    await handleUpdateVoucher({ status: 'archived' });
  }

  const pageActions: TableActionItem[] = voucher
    ? [
        ...(isVoucherEditable(voucher)
          ? [
              {
                id: 'edit-voucher',
                label: 'Edit voucher',
                icon: Pencil,
                placement: 'inline-priority',
                variant: 'primary',
                disabled: updating,
                onSelect: () => {
                  setUpdateError(null);
                  setEditing(true);
                },
              } satisfies TableActionItem,
            ]
          : []),
        ...(voucher.calculatedState === 'paused'
          ? [
              {
                id: 'expire-voucher',
                label: 'Expire voucher',
                icon: TimerOff,
                placement: 'menu',
                disabled: updating,
                isLoading: updating,
                onSelect: () => {
                  void handleExpireVoucher();
                },
              } satisfies TableActionItem,
            ]
          : []),
        ...(['expired'].includes(voucher.calculatedState) || isSingleUseRedeemed(voucher)
          ? [
              {
                id: 'archive-voucher',
                label: 'Archive voucher',
                icon: Archive,
                placement: 'menu',
                disabled: updating,
                isLoading: updating,
                onSelect: () => {
                  void handleArchiveVoucher();
                },
              } satisfies TableActionItem,
            ]
          : []),
        {
          id: 'copy-share-link',
          label: 'Copy share link',
          icon: Share2,
          placement: 'menu',
          onSelect: () => {
            const shareUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
            void copyTextToClipboard(shareUrl);
          },
        },
      ]
    : [];

  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        <PageHeader
          adornment={voucher ? <VoucherStatusChip state={voucher.calculatedState} /> : null}
          actions={
            <BackofficePageModeActionMenu
              pageMode={pageMode}
              onPageModeChange={setPageMode}
              viewActions={pageActions}
              label="Voucher actions"
              containerClassName="w-10 sm:w-[12.5rem]"
            />
          }
          showShareButton={false}
        >
          {voucher?.name ?? 'Voucher'}
        </PageHeader>

        {pageMode === 'edit' || detailTabs.customTabs.length > 0 ? (
          <CustomizableDetailTabs tabs={detailTabs} />
        ) : null}

        {loading ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-5 py-16 text-center text-body text-neutral-500 shadow-sm">
            Loading voucher
          </div>
        ) : error ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-5 py-16 text-center text-body text-red-700 shadow-sm">
            {error}
          </div>
        ) : voucher && detailTabs.activeCustomTab ? (
          <DetailPanelGrid
            resetKey={detailTabs.activeCustomTab.id}
            preferenceScope={`details:voucher:${detailTabs.activeCustomTab.id}`}
            layoutMode={pageMode}
            defaultVisibleItemIds={[]}
            tileCatalogLabel="Add voucher tile"
            items={[
              {
                id: 'voucher-details',
                title: 'Voucher details',
                layout: { x: 0, y: 0, w: 2, h: 3, minW: 1, minH: 2, isResizable: true },
                render: (layout) => (
                  <VoucherDetailsTile
                    voucher={voucher}
                    code={code}
                    panelId="voucher-details"
                    currentSpan={panelSpanFromLayout(layout, '2x3')}
                  />
                ),
              },
              {
                id: 'redeemed-by',
                title: 'Redeemed by',
                layout: { x: 2, y: 0, w: 2, h: 3, minW: 1, minH: 2, isResizable: true },
                render: (layout) => (
                  <RedeemedByTile
                    redemptions={redemptions}
                    panelId="redeemed-by"
                    currentSpan={panelSpanFromLayout(layout, '2x3')}
                  />
                ),
              },
            ]}
          />
        ) : voucher ? (
          <DetailPanelGrid
            resetKey={voucher.id}
            preferenceScope="details:voucher:overview"
            layoutMode={pageMode}
            tileCatalogLabel="Add voucher tile"
            items={[
              {
                id: 'voucher-details',
                title: 'Voucher details',
                layout: { x: 0, y: 0, w: 2, h: 3, minW: 1, minH: 2, isResizable: true },
                render: (layout) => (
                  <VoucherDetailsTile
                    voucher={voucher}
                    code={code}
                    panelId="voucher-details"
                    currentSpan={panelSpanFromLayout(layout, '2x3')}
                  />
                ),
              },
              {
                id: 'redeemed-by',
                title: 'Redeemed by',
                layout: { x: 2, y: 0, w: 2, h: 3, minW: 1, minH: 2, isResizable: true },
                render: (layout) => (
                  <RedeemedByTile
                    redemptions={redemptions}
                    panelId="redeemed-by"
                    currentSpan={panelSpanFromLayout(layout, '2x3')}
                  />
                ),
              },
            ]}
          />
        ) : null}

        {editing && voucher ? (
          <CreateVoucherModal
            mode="edit"
            voucher={voucher}
            code={code}
            submitting={updating}
            error={updateError}
            onClose={() => {
              if (!updating) setEditing(false);
            }}
            onSubmit={handleUpdateVoucher}
          />
        ) : null}
      </div>
    </main>
  );
}
